/**
 * Built-in workflow handlers for EntityForm lifecycle events.
 *
 * Provides handler functions for success, error, and cancel workflows
 * that are driven by the WorkflowConfig. Custom handlers registered
 * in the workflow registry are invoked when configured.
 *
 * @see spec sections 7.1, 7.4, 7.5
 */

import type {
  EntityFormConfig,
  UseEntityFormReturn,
} from '../types';
import { workflowRegistry } from './registry';

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

/** Result of processing a successful submission. */
export interface SubmitSuccessResult {
  /** Notification to display, if configured. */
  notification?: {
    messageKey: string;
    variant: 'success' | 'info';
  };
  /** Redirect target, if configured. */
  redirect?: {
    to: string;
    replace: boolean;
  };
}

/** Result of processing a submission error. */
export interface SubmitErrorResult {
  /** Form-level error message extracted from the API response. */
  formError: string;
  /** Field-level errors mapped from the API response body. */
  fieldErrors: Record<string, string>;
  /** Notification to display, if configured. */
  notification?: {
    messageKey: string;
    variant: 'error' | 'warning';
  };
}

/** Result of processing a cancel action. */
export interface CancelResult {
  /** Whether a confirmation dialog should be shown. */
  shouldShowConfirmation: boolean;
  /** Redirect target, if configured. */
  redirectTo?: {
    to: string;
    replace: boolean;
  };
  /** Confirmation dialog details when `shouldShowConfirmation` is true. */
  confirmationDetails?: {
    title: string;
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Success handler
// ---------------------------------------------------------------------------

/**
 * Processes the success workflow after a 2xx API response.
 *
 * - Builds a notification config if `onSuccess.notification` is set.
 * - Resets the form if `onSuccess.resetForm` is true.
 * - Resolves redirect path parameters from the API response.
 * - Calls a custom success handler from the registry if configured.
 *
 * @param response    - The raw API response.
 * @param config      - The full EntityForm configuration.
 * @param formActions - The form state/actions from useEntityForm.
 * @returns A result object describing what the caller should do.
 */
export function handleSubmitSuccess(
  response: unknown,
  config: EntityFormConfig,
  formActions: UseEntityFormReturn,
): SubmitSuccessResult {
  const workflow = config.workflow;
  const onSuccess = workflow?.onSuccess;
  const result: SubmitSuccessResult = {};

  // Notification
  if (onSuccess?.notification) {
    result.notification = {
      messageKey: onSuccess.notification.messageKey,
      variant: onSuccess.notification.variant ?? 'success',
    };
  }

  // Reset form
  if (onSuccess?.resetForm) {
    formActions.reset();
  }

  // Redirect (resolve :params from response)
  if (onSuccess?.redirect) {
    const resolvedPath = resolveRedirectParams(
      onSuccess.redirect.to,
      response,
    );
    result.redirect = {
      to: resolvedPath,
      replace: onSuccess.redirect.replace ?? false,
    };
  }

  // Custom handler from registry
  if (onSuccess?.custom) {
    const handler = workflowRegistry.getSuccessHandler(onSuccess.custom);
    if (handler) {
      handler(response, formActions);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Error handler
// ---------------------------------------------------------------------------

/**
 * Processes the error workflow when the API returns a 4xx/5xx response.
 *
 * - Extracts a human-readable error message from the response.
 * - Maps field-level errors from 422 responses when configured.
 * - Sets field errors on the form state.
 * - Builds a notification config if `onError.notification` is set.
 * - Calls a custom error handler from the registry if configured.
 *
 * @param error       - The error object from the API call.
 * @param config      - The full EntityForm configuration.
 * @param formActions - The form state/actions from useEntityForm.
 * @returns A result object describing what the caller should do.
 */
export function handleSubmitError(
  error: unknown,
  config: EntityFormConfig,
  formActions: UseEntityFormReturn,
): SubmitErrorResult {
  const workflow = config.workflow;
  const onError = workflow?.onError;

  // Extract top-level error message
  const formError = extractErrorMessage(error);

  // Map field errors from 422 response
  let fieldErrors: Record<string, string> = {};

  if (onError?.mapFieldErrors) {
    fieldErrors = mapApiFieldErrors(error, config.endpoint.requestMapping);

    // Apply field errors to the form state
    for (const [fieldName, message] of Object.entries(fieldErrors)) {
      formActions.setError(fieldName, message);
    }
  }

  const result: SubmitErrorResult = {
    formError,
    fieldErrors,
  };

  // Notification
  if (onError?.notification) {
    result.notification = {
      messageKey: onError.notification.messageKey,
      variant: onError.notification.variant ?? 'error',
    };
  }

  // Custom handler from registry
  if (onError?.custom) {
    const handler = workflowRegistry.getErrorHandler(onError.custom);
    if (handler) {
      handler(error, formActions);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Cancel handler
// ---------------------------------------------------------------------------

/**
 * Processes the cancel workflow when the user clicks Cancel.
 *
 * - Checks whether the form is dirty and a confirmation is configured.
 * - Returns redirect target if configured.
 * - Calls a custom cancel handler from the registry if configured.
 *
 * @param isDirty     - Whether the form has unsaved changes.
 * @param config      - The full EntityForm configuration.
 * @param formActions - Optional form state for custom cancel handlers.
 * @returns A result object describing what the caller should do.
 */
export function handleCancel(
  isDirty: boolean,
  config: EntityFormConfig,
  formActions?: UseEntityFormReturn,
): CancelResult {
  const onCancel = config.workflow?.onCancel;

  const result: CancelResult = {
    shouldShowConfirmation: false,
  };

  // Check if we need to show a confirmation dialog
  if (isDirty && onCancel?.confirmIfDirty) {
    result.shouldShowConfirmation = true;

    if (typeof onCancel.confirmIfDirty === 'object') {
      result.confirmationDetails = {
        title: onCancel.confirmIfDirty.title ?? 'Unsaved Changes',
        message:
          onCancel.confirmIfDirty.message ??
          'You have unsaved changes. Are you sure you want to leave?',
      };
    } else {
      result.confirmationDetails = {
        title: 'Unsaved Changes',
        message:
          'You have unsaved changes. Are you sure you want to leave?',
      };
    }
  }

  // Redirect
  if (onCancel?.redirect) {
    result.redirectTo = {
      to: onCancel.redirect.to,
      replace: onCancel.redirect.replace ?? false,
    };
  }

  // Custom handler from registry
  if (onCancel?.custom && formActions) {
    const handler = workflowRegistry.getCancelHandler(onCancel.custom);
    if (handler) {
      handler(formActions);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Field error mapping (422 responses)
// ---------------------------------------------------------------------------

/**
 * Extracts field-level errors from a 422 API response and reverse-maps
 * the field names using the request mapping.
 *
 * The expected API error format is:
 * ```json
 * {
 *   "message": "The given data was invalid.",
 *   "errors": {
 *     "email": ["The email has already been taken."],
 *     "role_id": ["The selected role is invalid."]
 *   }
 * }
 * ```
 *
 * When `requestMapping` is `{ role: "role_id" }`, the API field `role_id`
 * is reverse-mapped back to the form field `role`.
 *
 * @param errorResponse  - The raw error from the API call.
 * @param requestMapping - Optional mapping of form field name to API field name.
 * @returns A record of form field names to their first error message.
 */
export function mapApiFieldErrors(
  errorResponse: unknown,
  requestMapping?: Record<string, string>,
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  // Attempt to extract the errors object from the response
  const errorsObj = extractFieldErrorsObject(errorResponse);
  if (!errorsObj) {
    return fieldErrors;
  }

  // Build reverse mapping: api field name -> form field name
  const reverseMapping = buildReverseMapping(requestMapping);

  for (const [apiField, messages] of Object.entries(errorsObj)) {
    if (!Array.isArray(messages) || messages.length === 0) {
      continue;
    }

    const firstMessage = messages[0];
    if (typeof firstMessage !== 'string') {
      continue;
    }

    // Reverse-map the API field name to the form field name
    const formField = reverseMapping.get(apiField) ?? apiField;
    fieldErrors[formField] = firstMessage;
  }

  return fieldErrors;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Resolves `:param` placeholders in a redirect path using values from
 * the API response.
 *
 * @example
 * ```ts
 * resolveRedirectParams('/users/:id', { data: { id: 42 } });
 * // => '/users/42'
 * ```
 */
function resolveRedirectParams(path: string, response: unknown): string {
  if (!path.includes(':')) {
    return path;
  }

  return path.replaceAll(/:([a-zA-Z_]+)/g, (_match, paramName: string) => {
    const value = getNestedValue(response, paramName);
    return formatParamValue(value, paramName);
  });
}

/**
 * Formats a resolved parameter value for use in a redirect path.
 *
 * Returns the original `:paramName` placeholder when the value is missing,
 * uses `JSON.stringify` for objects, and `String()` for primitives.
 */
function formatParamValue(value: unknown, paramName: string): string {
  if (value === undefined || value === null) {
    return `:${paramName}`;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  // At this point value is guaranteed to be a primitive (string, number, boolean, bigint, symbol)
  // after the null/undefined and object checks above.
  return String(value as string | number | boolean);
}

/**
 * Safely retrieves a string property from an unknown value if it is a non-null object.
 */
function getStringProp(obj: unknown, key: string): string | undefined {
  /* v8 ignore start -- defensive guard; callers always pass objects */
  if (obj === null || typeof obj !== 'object') {
    return undefined;
  }
  /* v8 ignore stop */
  const value = (obj as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * Safely retrieves an object property from an unknown value if it is a non-null object.
 */
function getObjectProp(obj: unknown, key: string): Record<string, unknown> | undefined {
  /* v8 ignore start -- defensive guard; callers always pass objects */
  if (obj === null || typeof obj !== 'object') {
    return undefined;
  }
  /* v8 ignore stop */
  const value = (obj as Record<string, unknown>)[key];
  if (value !== null && typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  return undefined;
}

/**
 * Extracts a `message` string by walking into nested `data` properties.
 * Checks both `obj.data.message` and `obj.message`.
 */
function extractMessageFromObj(obj: Record<string, unknown>): string | undefined {
  const data = getObjectProp(obj, 'data');
  const dataMessage = data ? getStringProp(data, 'message') : undefined;
  if (dataMessage) {
    return dataMessage;
  }
  return getStringProp(obj, 'message');
}

/**
 * Extracts a human-readable error message from an API error.
 *
 * Attempts several common error shapes:
 * 1. `error.response.data.message`
 * 2. `error.data.message`
 * 3. `error.message`
 * 4. String error
 * 5. Fallback generic message
 */
function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error === null || typeof error !== 'object') {
    return 'An unexpected error occurred.';
  }

  const errorObj = error as Record<string, unknown>;

  // Shape: { response: { data: { message: string } } } (Axios-like)
  const response = getObjectProp(errorObj, 'response');
  if (response) {
    const msg = extractMessageFromObj(response);
    if (msg) {
      return msg;
    }
  }

  // Shape: { data: { message: string } } or { message: string }
  const msg = extractMessageFromObj(errorObj);
  if (msg) {
    return msg;
  }

  return 'An unexpected error occurred.';
}

/**
 * Extracts a non-array object at the `errors` key from a given object, or null.
 */
function getErrorsRecord(obj: Record<string, unknown>): Record<string, unknown> | null {
  const errors = getObjectProp(obj, 'errors');
  if (errors && !Array.isArray(errors)) {
    return errors;
  }
  return null;
}

/**
 * Extracts the `errors` object from a 422 API response.
 *
 * Handles shapes:
 * - `{ response: { data: { errors: {...} } } }` (Axios-like)
 * - `{ data: { errors: {...} } }` (direct response)
 * - `{ errors: {...} }` (plain object)
 */
function extractFieldErrorsObject(
  error: unknown,
): Record<string, unknown> | null {
  if (error === null || typeof error !== 'object') {
    return null;
  }

  const errorObj = error as Record<string, unknown>;

  // Axios-like shape: response.data.errors
  const response = getObjectProp(errorObj, 'response');
  if (response) {
    const data = getObjectProp(response, 'data');
    if (data) {
      const errors = getErrorsRecord(data);
      if (errors) {
        return errors;
      }
    }
  }

  // Direct response shape: data.errors
  const directData = getObjectProp(errorObj, 'data');
  if (directData) {
    const errors = getErrorsRecord(directData);
    if (errors) {
      return errors;
    }
  }

  // Plain object: errors
  return getErrorsRecord(errorObj);
}

/**
 * Builds a reverse mapping from API field names to form field names.
 *
 * Given `{ firstName: "first_name", role: "role_id" }`, produces a Map:
 * `first_name -> firstName`, `role_id -> role`
 */
function buildReverseMapping(
  requestMapping?: Record<string, string>,
): Map<string, string> {
  const reverse = new Map<string, string>();

  if (!requestMapping) {
    return reverse;
  }

  for (const [formField, apiField] of Object.entries(requestMapping)) {
    reverse.set(apiField, formField);
  }

  return reverse;
}

/**
 * Retrieves a value from a nested object using a simple key or dot-notation path.
 *
 * Checks both the root level and common nested shapes (`data.*`).
 */
function getNestedValue(obj: unknown, path: string): unknown {
  if (obj === null || typeof obj !== 'object') {
    return undefined;
  }

  const root = obj as Record<string, unknown>;

  // Try direct key first
  const directValue = root[path];
  if (directValue !== undefined) {
    return directValue;
  }

  // Try data.* path
  const data = root['data'];
  if (data !== null && typeof data === 'object') {
    const dataObj = data as Record<string, unknown>;
    const dataValue = dataObj[path];
    if (dataValue !== undefined) {
      return dataValue;
    }
  }

  // Try full dot-notation path
  const segments = path.split('.');
  let current: unknown = obj;

  for (const segment of segments) {
    /* v8 ignore start -- unreachable; redirect param regex cannot produce dot-separated paths */
    if (current === null || typeof current !== 'object') {
      return undefined;
    }
    /* v8 ignore stop */
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}
